<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getContrastingTextColor } from '@chatwoot/utils';
import { getLabel, getPlaceHolder } from 'dashboard/helper/preChat';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  fields: {
    type: Array,
    default: () => [],
  },
  color: {
    type: String,
    default: '#009CE0',
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const enabledFields = computed(() =>
  props.fields.filter(field => field.enabled)
);

const headerMessage = computed(() =>
  (props.message || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()
);

const buttonTextColor = computed(() => getContrastingTextColor(props.color));

const resolveInputType = field => {
  if (field.name === 'phoneNumber') return 'tel';
  if (field.type === 'link') return 'url';
  if (field.type === 'email') return 'email';
  if (field.type === 'number') return 'number';
  return 'text';
};

const isSelectField = field => field.type === 'select' || field.type === 'list';

const isTextAreaField = field => field.type === 'text_area';
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col bg-n-background dark:bg-n-solid-1">
    <div class="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-3">
      <p v-if="headerMessage" class="mb-4 text-sm leading-5 text-n-slate-12">
        {{ headerMessage }}
      </p>

      <div class="flex flex-col gap-3">
        <label
          v-for="field in enabledFields"
          :key="field.name"
          class="flex flex-col gap-1"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ getLabel({ key: field.name, label: field.label }) }}
            <span v-if="field.required" class="text-n-ruby-9">*</span>
          </span>

          <select
            v-if="isSelectField(field)"
            disabled
            class="h-9 w-full rounded-md border border-n-weak bg-n-slate-2 px-3 text-sm text-n-slate-11"
          >
            <option value="">
              {{
                getPlaceHolder({
                  key: field.name,
                  placeholder: field.placeholder,
                })
              }}
            </option>
            <option
              v-for="(option, index) in field.values || []"
              :key="`${field.name}-${index}`"
              :value="option"
            >
              {{ option }}
            </option>
          </select>

          <textarea
            v-else-if="isTextAreaField(field)"
            disabled
            rows="3"
            class="w-full resize-none rounded-md border border-n-weak bg-n-slate-2 px-3 py-2 text-sm text-n-slate-11"
            :placeholder="
              getPlaceHolder({
                key: field.name,
                placeholder: field.placeholder,
              })
            "
          />

          <input
            v-else-if="field.type === 'checkbox'"
            disabled
            type="checkbox"
            class="size-4 rounded border border-n-weak"
          />

          <input
            v-else
            disabled
            :type="resolveInputType(field)"
            class="h-9 w-full rounded-md border border-n-weak bg-n-slate-2 px-3 text-sm text-n-slate-11"
            :placeholder="
              getPlaceHolder({
                key: field.name,
                placeholder: field.placeholder,
              })
            "
          />
        </label>
      </div>
    </div>

    <div class="mt-auto shrink-0 px-4 pb-1">
      <button
        type="button"
        class="h-10 w-full cursor-pointer rounded-md px-4 text-sm font-medium"
        :style="{ backgroundColor: color, color: buttonTextColor }"
        @click="emit('submit')"
      >
        {{
          t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.START_CONVERSATION_BUTTON_TEXT')
        }}
      </button>
    </div>
  </div>
</template>
